import { useEffect, useRef, useState } from 'react';
import { BordOneToneIcon } from 'components/NudosComponents';
import ResponsiblesDropdown from './components/ResponsiblesDropdown/ResponsiblesDropdown';
import { IQuoteResponsibleUser } from 'types/quote.types';
import useUsersState from 'state/useUsersState';
import './AddResponsiblesButton.scss';

interface IAddResponsiblesButton {
  responsibles?: IQuoteResponsibleUser[];
  quoteId?: number;
  onResponsiblesUpdate?: () => void;
  disabled?: boolean;
}

/**
 * Botón para agregar responsables a una cotización.
 * Al hacer click abre un dropdown de búsqueda para asignar o quitar responsables.
 * Si ya hay responsables, muestra avatares apilados, conteo restante y tooltip con la lista completa.
 */
const AddResponsiblesButton = ({ responsibles, quoteId, onResponsiblesUpdate, disabled }: IAddResponsiblesButton) => {
  const { users } = useUsersState();
  const [isHovered, setIsHovered] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showAvatarTooltip, setShowAvatarTooltip] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const tooltipTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const handleClickOutside = (event: MouseEvent) => {
    if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) setShowDropdown(false);
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const maxVisibleAvatars = 3;
  const defaultIconStroke = '#6F737D';
  const hoverIconStroke = '#00B188';

  const assignedUsers = responsibles?.map(ru => users.find(u => u.id === ru.userId)).filter(Boolean) ?? [];
  const hasResponsibles = !!assignedUsers.length;
  const visibleAvatars = assignedUsers.slice(0, maxVisibleAvatars);
  const remainingCount = assignedUsers.length - maxVisibleAvatars;
  const isActive = isHovered || showDropdown;
  const iconStroke = isActive ? hoverIconStroke : defaultIconStroke;

  const handleAvatarMouseEnter = () => {
    if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current);
    setShowAvatarTooltip(true);
  };

  const handleAvatarMouseLeave = () => {
    tooltipTimeoutRef.current = setTimeout(() => setShowAvatarTooltip(false), 120);
  };

  return (
    <div className="addResponsiblesButtonWrapper" ref={wrapperRef}>
      <button
        className={`addResponsiblesButton${hasResponsibles ? ' withResponsibles' : ''}${isActive ? ' isActive' : ''}`}
        onClick={() => setShowDropdown(prev => !prev)}
        disabled={disabled}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {hasResponsibles && (
          <div className="avatarStack" onMouseEnter={handleAvatarMouseEnter} onMouseLeave={handleAvatarMouseLeave}>
            {visibleAvatars.map((user, index) =>
              user?.photoProfile ? (
                <img
                  key={user.id}
                  className="avatar"
                  src={user.photoProfile}
                  alt={`${user.firstName} ${user.lastName}`}
                  style={{ zIndex: maxVisibleAvatars - index }}
                />
              ) : (
                <div key={user?.id} className="avatarPlaceholder" style={{ zIndex: maxVisibleAvatars - index }}>
                  <BordOneToneIcon variant="user" standardSize={16} stroke="#B4B4B4" />
                </div>
              )
            )}
          </div>
        )}
        {hasResponsibles && remainingCount > 0 && <div className="remainingCount">+{remainingCount}</div>}
        <div className="buttonLabel">Agregar responsables</div>
        <BordOneToneIcon variant="plusCircle" standardSize={14} stroke={iconStroke} className="addIcon" />
      </button>
      {showAvatarTooltip && !showDropdown && hasResponsibles && (
        <div
          className="assignedUsersTooltip"
          onMouseEnter={handleAvatarMouseEnter}
          onMouseLeave={handleAvatarMouseLeave}
        >
          <div className="tooltipScrollArea">
            {assignedUsers.map(user => (
              <div key={user?.id} className="tooltipUserRow">
                {user?.photoProfile ? (
                  <img className="tooltipAvatar" src={user.photoProfile} alt={`${user.firstName} ${user.lastName}`} />
                ) : (
                  <div className="tooltipAvatarPlaceholder">
                    <BordOneToneIcon variant="user" standardSize={14} stroke="#B4B4B4" />
                  </div>
                )}
                <div className="tooltipUserInfo">
                  <div className="tooltipUserName">
                    {user?.firstName} {user?.lastName}
                  </div>
                  <div className="tooltipUserEmail">{user?.email}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {showDropdown && (
        <ResponsiblesDropdown
          quoteId={quoteId}
          responsibles={responsibles ?? []}
          onResponsiblesUpdate={onResponsiblesUpdate}
        />
      )}
    </div>
  );
};

export default AddResponsiblesButton;
